import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ProductGrid.css';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const NoProductsMessage = () => (
  <div className="no-products-message">
    <i className="fas fa-store"></i>
    <p>Valid Shopify store found, but no free products are currently available.</p>
  </div>
);

const NoImagePlaceholder = () => (
  <svg width="80" height="80" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#2a2a2a"/>
    <g transform="translate(8, 8) scale(0.7)">
      <circle 
        cx="10" 
        cy="10" 
        r="8" 
        stroke="#444" 
        stroke-width="3" 
        fill="none"
      />
      <rect 
        x="16" 
        y="16" 
        width="8" 
        height="3" 
        rx="1.5" 
        transform="rotate(45 16 16)" 
        fill="#444"
      />
    </g>
  </svg>
);

const ProductImage = ({ src, alt }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className="product-image">
      {isLoading && <div className="image-skeleton" />}
      {src ? (
        hasError ? (
          <div className="image-error">
            <i className="fas fa-image"></i>
            <span>Failed to load image</span>
          </div>
        ) : (
          <img 
            src={src} 
            alt={alt}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            className={isLoading ? 'image-hidden' : 'image-loaded'}
          />
        )
      ) : (
        <div className="no-image">
          <NoImagePlaceholder />
          <span>No Image Available</span>
        </div>
      )}
    </div>
  );
};

const formatPrice = (price) => {
  const num = parseFloat(price);
  if (num === 0.00) return 'FREE';
  return `$${num.toFixed(2)}`;
};

const ProductVariants = ({ variants, getCartUrl, productUrl, onQuantityChange, onVariantChange, onAddToMultiCart }) => {
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(() => {
    const firstAvailableVariant = variants.find(v => v.available);
    return firstAvailableVariant || variants[0];
  });
  const [addedFlash, setAddedFlash] = useState(false);

  const handleQuantityChange = (e) => {
    const quantity = parseInt(e.target.value);
    setSelectedQuantity(quantity);
    onQuantityChange(quantity, selectedVariant?.id || variants[0].id);
  };

  const handleVariantChange = (e) => {
    const variantId = e.target.value;
    const variant = variants.find(v => String(v.id) === String(variantId));
    if (variant) {
      setSelectedVariant(variant);
      onQuantityChange(selectedQuantity, variant.id);
      onVariantChange(variant);
    }
  };

  const handleAddToMultiCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const variant = selectedVariant || variants[0];
    if (!variant.available) return;
    onAddToMultiCart({
      variantId: variant.id,
      quantity: selectedQuantity,
      variantTitle: variant.title,
      price: variant.price
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 600);
  };

  const QuantityDropdown = () => (
    <select 
      className="quantity-select"
      value={selectedQuantity}
      onChange={handleQuantityChange}
      onClick={(e) => e.stopPropagation()}
    >
      {[1, 2, 3, 4, 5, 10].map(num => (
        <option key={num} value={num}>{num}</option>
      ))}
    </select>
  );

  const getVariantPrice = (variant) => formatPrice(variant?.price || '0.00');
  const currentVariant = selectedVariant || variants[0];
  const isAvailable = currentVariant.available;

  return (
    <div className="variant-info">
      {variants.length > 1 ? (
        <>
          <div className="variants-header">
            <span className="variants-count">{variants.length} Options Available</span>
            {variants.some(v => v.available) && (
              <span className={`price ${parseFloat(currentVariant.price) !== 0 ? 'price-paid' : ''}`}>
                {getVariantPrice(currentVariant)}
              </span>
            )}
          </div>
          <div className="variant-selection">
            <select 
              className="variant-select"
              value={currentVariant.id || ''}
              onChange={handleVariantChange}
            >
              {variants.map(variant => (
                <option 
                  key={variant.id} 
                  value={variant.id}
                >
                  {variant.title !== 'Default Title' ? variant.title : 'Select Option'} {!variant.available ? '(Out of Stock)' : ''} — {formatPrice(variant.price)}
                </option>
              ))}
            </select>
            <div className="variant-row">
              <a 
                href={getCartUrl(productUrl.split('/products/')[0], currentVariant.id, selectedQuantity)}
                target="_blank"
                rel="noopener noreferrer"
                className={`cart-link ${!isAvailable ? 'out-of-stock' : ''}`}
              >
                {isAvailable ? 'Add to Cart' : 'Out of Stock'}
              </a>
              {isAvailable && <QuantityDropdown />}
              {isAvailable && (
                <button
                  className={`multicart-add-btn ${addedFlash ? 'added' : ''}`}
                  onClick={handleAddToMultiCart}
                  title="Add to Multi-Cart"
                >
                  <i className={`fas ${addedFlash ? 'fa-check' : 'fa-plus'}`}></i>
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <span className="variant-title">
            {variants[0].title !== 'Default Title' ? variants[0].title : ''}
          </span>
          {variants[0].available && (
            <span className={`price ${parseFloat(variants[0].price) !== 0 ? 'price-paid' : ''}`}>
              {formatPrice(variants[0].price)}
            </span>
          )}
          <div className="variant-row">
            <a 
              href={getCartUrl(productUrl.split('/products/')[0], variants[0].id, selectedQuantity)}
              target="_blank"
              rel="noopener noreferrer"
              className={`cart-link ${!variants[0].available ? 'out-of-stock' : ''}`}
            >
              {variants[0].available ? 'Add to Cart' : 'Out of Stock'}
            </a>
            {variants[0].available && <QuantityDropdown />}
            {variants[0].available && (
              <button
                className={`multicart-add-btn ${addedFlash ? 'added' : ''}`}
                onClick={handleAddToMultiCart}
                title="Add to Multi-Cart"
              >
                <i className={`fas ${addedFlash ? 'fa-check' : 'fa-plus'}`}></i>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ProductCard = ({ product, getCartUrl, onAddToMultiCart }) => {
  const [currentVariantId, setCurrentVariantId] = useState(() => {
    const firstAvailableVariant = product.freeVariants.find(v => v.available);
    return firstAvailableVariant?.id || product.freeVariants[0].id;
  });
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const [showCopied, setShowCopied] = useState(false);
  const [currentImage, setCurrentImage] = useState(product.featuredImage);

  const handleQuantityChange = (quantity, variantId) => {
    setCurrentQuantity(quantity);
    setCurrentVariantId(variantId);
  };

  const handleVariantChange = (variant) => {
    if (variant.image) {
      setCurrentImage(variant.image);
    } else {
      setCurrentImage(product.featuredImage);
    }
  };

  const getCartLink = () => {
    const storeUrl = product.productUrl.split('/products/')[0];
    return getCartUrl(storeUrl, currentVariantId, currentQuantity);
  };

  const handleCopyCartLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getCartLink())
      .then(() => {
        setShowCopied(true);
        setTimeout(() => {
          setShowCopied(false);
        }, 2000);
      })
      .catch(console.error);
  };

  const handleAddToMultiCart = (variantInfo) => {
    const storeUrl = product.productUrl.split('/products/')[0];
    onAddToMultiCart({
      ...variantInfo,
      productTitle: product.title,
      storeUrl
    });
  };

  return (
    <div className="product-card">
      <div className="product-image-container">
        <ProductImage src={currentImage} alt={product.title} />
        <div className="share-buttons">
          <a 
            className="share-button" 
            href={product.productUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open product page"
          >
            <i className="fas fa-external-link-alt"></i>
          </a>
          <button 
            className="share-button" 
            onClick={handleCopyCartLink}
            title="Copy cart link"
          >
            <i className={`fas ${showCopied ? 'fa-check' : 'fa-link'}`}></i>
          </button>
        </div>
      </div>
      <div className="product-info">
        <h4 className="product-title">{product.title}</h4>
        <ProductVariants 
          variants={product.freeVariants}
          getCartUrl={getCartUrl}
          productUrl={product.productUrl}
          onQuantityChange={handleQuantityChange}
          onVariantChange={handleVariantChange}
          onAddToMultiCart={handleAddToMultiCart}
        />
      </div>
    </div>
  );
};

const ProductPagination = ({ currentPage, totalPages, onPageChange, totalProducts, startIndex, productsPerPage }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="product-pagination">
      <div className="pagination-info">
        Showing {startIndex + 1}-{Math.min(startIndex + productsPerPage, totalProducts)} of {totalProducts} products
      </div>
      <div className="pagination">
        <button 
          className="page-button"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-double-left"></i>
        </button>
        <button 
          className="page-button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-left"></i>
        </button>
        
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>

        <button 
          className="page-button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-right"></i>
        </button>
        <button 
          className="page-button"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-double-right"></i>
        </button>
      </div>
    </div>
  );
};

const MultiCartBar = ({ items, onRemove, onClear, onUpdateQuantity }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const barRef = React.useRef(null);

  useEffect(() => {
    if (items.length === 0) {
      document.body.style.paddingBottom = '';
      return;
    }
    const updatePadding = () => {
      if (barRef.current) {
        document.body.style.paddingBottom = `${barRef.current.offsetHeight}px`;
      }
    };
    updatePadding();
    const observer = new ResizeObserver(updatePadding);
    if (barRef.current) observer.observe(barRef.current);
    return () => {
      observer.disconnect();
      document.body.style.paddingBottom = '';
    };
  }, [items.length, isExpanded]);

  if (items.length === 0) return null;

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const storeUrl = items[0].storeUrl.replace(/\/+$/, '');
  const cartParts = items.map(item => `${item.variantId}:${item.quantity}`);
  const checkoutUrl = `${storeUrl}/cart/${cartParts.join(',')}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(checkoutUrl).catch(console.error);
  };

  return (
    <div ref={barRef} className={`multicart-bar ${isExpanded ? 'expanded' : ''}`}>
      <div className="multicart-bar-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="multicart-bar-info">
          <i className="fas fa-shopping-cart"></i>
          <span className="multicart-badge">{totalQuantity}</span>
          <span className="multicart-summary">
            {items.length} {items.length === 1 ? 'item' : 'items'} in Multi-Cart
          </span>
        </div>
        <div className="multicart-bar-actions">
          <button className="multicart-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopyLink(); }} title="Copy cart link">
            <i className="fas fa-link"></i>
          </button>
          <button className="multicart-clear-btn" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear cart">
            <i className="fas fa-trash"></i>
          </button>
          <a
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="multicart-checkout-btn"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-external-link-alt"></i> Checkout
          </a>
          <button className="multicart-expand-btn" onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'up'}`}></i>
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="multicart-items">
          {items.map((item, index) => (
            <div key={`${item.variantId}-${index}`} className="multicart-item">
              <div className="multicart-item-info">
                <span className="multicart-item-title">{item.productTitle}</span>
                {item.variantTitle && item.variantTitle !== 'Default Title' && (
                  <span className="multicart-item-variant">{item.variantTitle}</span>
                )}
                <span className={`multicart-item-price ${parseFloat(item.price) === 0 ? '' : 'price-paid'}`}>
                  {formatPrice(item.price)}
                </span>
              </div>
              <div className="multicart-item-controls">
                <button onClick={() => onUpdateQuantity(index, Math.max(1, item.quantity - 1))} className="multicart-qty-btn">
                  <i className="fas fa-minus"></i>
                </button>
                <span className="multicart-item-qty">{item.quantity}</span>
                <button onClick={() => onUpdateQuantity(index, item.quantity + 1)} className="multicart-qty-btn">
                  <i className="fas fa-plus"></i>
                </button>
                <button onClick={() => onRemove(index)} className="multicart-remove-btn" title="Remove">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductGrid = ({ products, allProducts, isValidStore = false }) => {
  const [gridSize, setGridSize] = useState('4');
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [showAllProducts, setShowAllProducts] = useState(() => {
    return localStorage.getItem('showAllProducts') === 'true';
  });
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('defaultSort') || 'products');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 150);
  const productsPerPage = 100;
  const [multiCart, setMultiCart] = useState([]);

  const activeProducts = showAllProducts && allProducts ? allProducts : products;

  const handleToggleAllProducts = useCallback(() => {
    setShowAllProducts(prev => {
      const newValue = !prev;
      localStorage.setItem('showAllProducts', newValue.toString());
      return newValue;
    });
  }, []);

  const handleAddToMultiCart = useCallback((item) => {
    setMultiCart(prev => {
      const existing = prev.findIndex(i => String(i.variantId) === String(item.variantId));
      if (existing !== -1) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + item.quantity };
        return updated;
      }
      return [...prev, item];
    });
  }, []);

  const handleRemoveFromMultiCart = useCallback((index) => {
    setMultiCart(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdateMultiCartQuantity = useCallback((index, newQuantity) => {
    setMultiCart(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: newQuantity };
      return updated;
    });
  }, []);

  const handleClearMultiCart = useCallback(() => {
    setMultiCart([]);
  }, []);

  useEffect(() => {
    const storedGridSize = localStorage.getItem('gridSize') || '4';
    const storedShowOutOfStock = localStorage.getItem('showOutOfStock');
    const showOutOfStockSetting = storedShowOutOfStock === null ? true : storedShowOutOfStock === 'true';
    const storedSortOrder = localStorage.getItem('defaultSort') || 'products';
    
    setGridSize(storedGridSize);
    setShowOutOfStock(showOutOfStockSetting);
    setSortOrder(storedSortOrder);

    if (!activeProducts) return;
    
    let filtered = [...activeProducts];

    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(query)
      );
    }

    const getLowestPrice = (product) => {
      return Math.min(...product.freeVariants.map(v => parseFloat(v.price)));
    };

    filtered = filtered.sort((a, b) => {
      if (storedSortOrder === 'store') {
        const storeA = new URL(a.productUrl).hostname.toLowerCase();
        const storeB = new URL(b.productUrl).hostname.toLowerCase();
        return storeA.localeCompare(storeB);
      }
      if (storedSortOrder === 'priceDesc') {
        return getLowestPrice(b) - getLowestPrice(a);
      }
      if (storedSortOrder === 'priceAsc') {
        return getLowestPrice(a) - getLowestPrice(b);
      }
      return b.freeVariants.length - a.freeVariants.length;
    });

    if (!showOutOfStockSetting) {
      filtered = filtered.filter(product => 
        product.freeVariants.some(variant => variant.available)
      );
    }
    
    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [activeProducts, showOutOfStock, sortOrder, debouncedSearchQuery]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  if (!activeProducts || activeProducts.length === 0) {
    return isValidStore ? (
      <div className="product-grid">
        {allProducts && allProducts.length > 0 && (
          <div className="grid-header">
            <h3 className="grid-title">
              {showAllProducts ? 'All Products' : 'Free Products Found'} (0)
            </h3>
            <label className="show-all-toggle" title="Toggle to view all products, not just free ones">
              <span className="toggle-label-text">Show All Products</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={showAllProducts}
                  onChange={handleToggleAllProducts}
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>
        )}
        <div className="tips-section">
          <h3>Tips for Finding Free Products</h3>
          <ul>
            <li>
              <i className="fas fa-check-circle"></i>
              Try searching for popular brands and stores
            </li>
            <li>
              <i className="fas fa-check-circle"></i>
              Check stores during major shopping events
            </li>
            <li>
              <i className="fas fa-check-circle"></i>
              Look for newly launched Shopify stores
            </li>
            <li>
              <i className="fas fa-check-circle"></i>
              Some stores may require shipping costs
            </li>
          </ul>
        </div>
        <NoProductsMessage />
      </div>
    ) : null;
  }

  const getCartUrl = (storeUrl, variantId, quantity = 1) => {
    const cleanStoreUrl = storeUrl.replace(/\/+$/, '');
    return `${cleanStoreUrl}/cart/${variantId}:${quantity}`;
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of results
    window.scrollTo({
      top: document.querySelector('.product-grid')?.offsetTop - 20 || 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="product-grid">
      <div className="grid-header">
        <h3 className="grid-title">
          {showAllProducts ? 'All Products' : 'Free Products Found'} ({filteredProducts.length})
        </h3>
        <label className="show-all-toggle" title="Toggle to view all products, not just free ones">
          <span className="toggle-label-text">Show All Products</span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              checked={showAllProducts}
              onChange={handleToggleAllProducts}
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
      </div>

      {filteredProducts.length > productsPerPage && (
        <ProductPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalProducts={filteredProducts.length}
          startIndex={startIndex}
          productsPerPage={productsPerPage}
        />
      )}

      <div className="search-container">
        <div className="search-input-wrapper">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={handleClearSearch}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      <div className={`grid-container size-${gridSize}`}>
        {paginatedProducts.map(product => (
          <ProductCard 
            key={product.id}
            product={product}
            getCartUrl={getCartUrl}
            onAddToMultiCart={handleAddToMultiCart}
          />
        ))}
      </div>

      {filteredProducts.length > productsPerPage && (
        <ProductPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalProducts={filteredProducts.length}
          startIndex={startIndex}
          productsPerPage={productsPerPage}
        />
      )}

      {createPortal(
        <MultiCartBar
          items={multiCart}
          onRemove={handleRemoveFromMultiCart}
          onClear={handleClearMultiCart}
          onUpdateQuantity={handleUpdateMultiCartQuantity}
        />,
        document.body
      )}
    </div>
  );
};

export default ProductGrid; 